/**
 * Registrar un descuento pactado con un proveedor/contratista.
 * Motivo, valor, proyecto y capítulo son obligatorios: el descuento se le resta al
 * capítulo elegido (y por ende al presupuesto del proyecto) apenas queda registrado.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import './ModalDescuentoProveedor.css';

const fmt = (n) => '$' + Math.round(Number(n) || 0).toLocaleString('es-CO');
const hoyISO = () => new Date().toISOString().split('T')[0];

const ModalDescuentoProveedor = ({ proveedor, onCerrar, onGuardado }) => {
  const { success, error } = useNotification();
  const [proyectos, setProyectos] = useState([]);
  const [capitulos, setCapitulos] = useState([]);
  const [cargandoCaps, setCargandoCaps] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({ proyecto_id: '', capitulo_id: '', motivo: '', valor: '', fecha: hoyISO() });

  useEffect(() => {
    (async () => {
      try {
        const d = await client.query(`{ proyectos { id proyecto_nombre apartamento estado } }`);
        const lista = d.proyectos || [];
        // Los liquidados de hace rato solo hacen ruido: primero los activos.
        const orden = { en_ejecucion: 0, contratado_no_iniciado: 1, vendido: 2 };
        setProyectos([...lista].sort((a, b) =>
          (orden[a.estado] ?? 9) - (orden[b.estado] ?? 9) ||
          String(a.proyecto_nombre).localeCompare(String(b.proyecto_nombre))));
      } catch { error('No se pudieron cargar los proyectos'); }
    })();
  }, [error]);

  const cargarCapitulos = useCallback(async (proyectoId) => {
    if (!proyectoId) { setCapitulos([]); return; }
    setCargandoCaps(true);
    try {
      const d = await client.query(
        `query($id: Int!) { proyecto(id: $id) { capitulos { id numero capitulo_nombre subtotal valor_ejecutado_total } } }`,
        { id: parseInt(proyectoId) });
      setCapitulos(d.proyecto?.capitulos || []);
    } catch { error('No se pudieron cargar los capítulos'); setCapitulos([]); }
    finally { setCargandoCaps(false); }
  }, [error]);

  const cambiarProyecto = (id) => {
    setForm(f => ({ ...f, proyecto_id: id, capitulo_id: '' }));
    cargarCapitulos(id);
  };

  const valorNum = parseFloat(String(form.valor).replace(/[^\d.-]/g, '')) || 0;
  const capSel = capitulos.find(c => String(c.id) === String(form.capitulo_id));
  const listo = form.proyecto_id && form.capitulo_id && form.motivo.trim() && valorNum > 0;

  const guardar = async () => {
    if (!listo) { error('Completa motivo, valor, proyecto y capítulo'); return; }
    setGuardando(true);
    try {
      const r = await client.mutate(
        `mutation($p: Int!, $py: Int!, $c: Int!, $m: String!, $v: Float!, $f: String) {
          crearDescuentoProveedor(proveedor_id: $p, proyecto_id: $py, capitulo_id: $c, motivo: $m, valor: $v, fecha: $f) { success message id } }`,
        {
          p: proveedor.id, py: parseInt(form.proyecto_id), c: parseInt(form.capitulo_id),
          m: form.motivo.trim(), v: valorNum, f: form.fecha || null,
        });
      if (r.crearDescuentoProveedor.success) {
        success(`Descuento de ${fmt(valorNum)} registrado`);
        onGuardado && onGuardado();
        onCerrar();
      } else {
        error(r.crearDescuentoProveedor.message || 'No se pudo registrar');
        setGuardando(false);
      }
    } catch { error('Error al registrar el descuento'); setGuardando(false); }
  };

  return (
    <div className="dp-overlay" onClick={() => !guardando && onCerrar()}>
      <div className="dp-modal" onClick={e => e.stopPropagation()}>
        <div className="dp-header">
          <div>
            <h3>Registrar descuento</h3>
            <span className="dp-prov">{proveedor.nombre}</span>
          </div>
          <button className="dp-cerrar" onClick={onCerrar} disabled={guardando}>×</button>
        </div>

        <div className="dp-body">
          <div className="dp-fila">
            <label className="dp-campo">
              <span>Proyecto <b>*</b></span>
              <select value={form.proyecto_id} onChange={e => cambiarProyecto(e.target.value)}>
                <option value="">Selecciona el proyecto...</option>
                {proyectos.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.proyecto_nombre}{p.apartamento ? ` · ${p.apartamento}` : ''}
                  </option>
                ))}
              </select>
            </label>

            <label className="dp-campo">
              <span>Capítulo <b>*</b></span>
              <select
                value={form.capitulo_id}
                disabled={!form.proyecto_id || cargandoCaps}
                onChange={e => setForm(f => ({ ...f, capitulo_id: e.target.value }))}
              >
                <option value="">
                  {!form.proyecto_id ? 'Primero elige el proyecto' : cargandoCaps ? 'Cargando...' : 'Selecciona el capítulo...'}
                </option>
                {capitulos.map(c => (
                  <option key={c.id} value={c.id}>{c.numero}. {c.capitulo_nombre}</option>
                ))}
              </select>
            </label>
          </div>

          {capSel && (
            <div className="dp-cap-info">
              Gasto actual del capítulo: <strong>{fmt(capSel.valor_ejecutado_total)}</strong>
              {valorNum > 0 && (
                <> → quedaría en <strong className="dp-nuevo">{fmt(Number(capSel.valor_ejecutado_total || 0) - valorNum)}</strong></>
              )}
            </div>
          )}

          <div className="dp-fila">
            <label className="dp-campo dp-campo-valor">
              <span>Valor del descuento <b>*</b></span>
              <input
                type="text" inputMode="numeric" placeholder="0"
                value={form.valor}
                onChange={e => setForm(f => ({ ...f, valor: e.target.value.replace(/[^\d]/g, '') }))}
              />
              {valorNum > 0 && <small className="dp-hint">{fmt(valorNum)}</small>}
            </label>
            <label className="dp-campo dp-campo-fecha">
              <span>Fecha</span>
              <input type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
            </label>
          </div>

          <label className="dp-campo">
            <span>Motivo <b>*</b></span>
            <textarea
              rows={3} maxLength={500} placeholder="Por qué se le descuenta al proveedor..."
              value={form.motivo}
              onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))}
            />
          </label>

          <p className="dp-nota">
            El descuento baja el gasto de ese capítulo desde ya. Cuando se aplique a una
            factura de este proveedor, la factura entrará por el valor neto y el saldo
            bajará: el gasto no cambia dos veces.
          </p>
        </div>

        <div className="dp-footer">
          <button className="dp-btn" onClick={onCerrar} disabled={guardando}>Cancelar</button>
          <button className="dp-btn dp-btn-ok" onClick={guardar} disabled={!listo || guardando}>
            {guardando ? 'Guardando...' : 'Registrar descuento'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalDescuentoProveedor;
